import React, { useState } from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { BottomTabNavigationProp } from '@react-navigation/bottom-tabs';

import { DonutChart } from '../../../components/charts/DonutChart';
import { InvestmentsVsDebtCard } from '../../../components/charts/InvestmentsVsDebtCard';
import { formatCurrency } from '../../../components/charts/d3/format';
import {
  EditTakeHomeModal,
  HomeLoanCard,
  NetWorthStrip,
  PortfolioSnapshot,
  SpendSnapshotPeriodFilter,
} from '../../../components/home';
import { PageHeader } from '../../../components/layout/PageHeader';
import { WebScreenBody } from '../../../components/layout/WebScreenBody';
import { WebWidgetCell, WebWidgetGrid } from '../../../components/layout/WebWidgetGrid';
import { WidgetRefreshScope } from '../../../contexts/WidgetRefreshContext';
import { InsightStrip } from '../../../components/assistant/InsightStrip';
import { PinnedWidgetsStrip } from '../../../components/assistant/PinnedWidgetsStrip';
import { WidgetInsightsSlot } from '../../../components/assistant/WidgetInsightCallout';
import { CreditCardDetailModal } from '../../../components/spend/CreditCardDetailModal';
import { CreditCardsWidget } from '../../../components/spend/CreditCardsWidget';
import { formatCardTitle } from '../../../components/spend/creditCardFormat';
import { HomeSpendAlertsCard } from '../../../components/spend/HomeSpendAlertsCard';
import { SpendImportCta } from '../../../components/spend/SpendImportCta';
import { ErrorRetry } from '../../../components/ui/ErrorRetry';
import { GlassButton } from '../../../components/ui/GlassButton';
import { GlassCard } from '../../../components/ui/GlassCard';
import { RefreshButton } from '../../../components/ui/RefreshButton';
import { SampleDataBanner } from '../../../components/ui/SampleDataBanner';
import { PortfolioAccountsCard } from '../../../components/wealth';
import { useImportOnboarding } from '../../../contexts/ImportOnboardingContext';
import { usePortfolio } from '../../../contexts/PortfolioContext';
import { useSampleData } from '../../../contexts/SampleDataContext';
import {
  navigateToLoan,
  navigateToSpend,
  navigateToWealth,
} from '../../../core/navigation/mainTabLinks';
import type { MainTabParamList } from '../../../core/navigation/types';
import { colors, spacing, typography } from '../../../core/theme/tokens';
import { useAppRefresh } from '../../../hooks/useAppRefresh';
import {
  homeSpendPeriodLabel,
  useHomeDashboard,
} from '../../../hooks/useHomeDashboard';
import type { CardSpendRow } from '../../../types/analytics';

export function HomeTabScreen() {
  const navigation = useNavigation<BottomTabNavigationProp<MainTabParamList>>();
  const { isSampleMode, sampleSpendTree } = useSampleData();
  const { openImportMore } = useImportOnboarding();
  const { openAddAccount } = usePortfolio();
  const [takeHomeOpen, setTakeHomeOpen] = useState(false);
  const [cardTarget, setCardTarget] = useState<CardSpendRow | null>(null);
  const {
    profile,
    summaries,
    netWorth,
    projections,
    loading,
    error,
    isLive,
    usingDraftPreview,
    spendPeriod,
    spendPeriodKey,
    setSpendPeriod,
    refresh,
  } = useHomeDashboard();
  const { refreshAll, refreshing } = useAppRefresh();

  const isEmpty = summaries.isEmpty;
  const spendCategories = summaries.categories;
  const spendPeriodLabel = homeSpendPeriodLabel(spendPeriod);
  const showSpendSnapshot = isLive || spendCategories.length > 0;
  const missingTakeHome = isLive && profile.monthlyIncome <= 0;
  const openSpend = () =>
    navigateToSpend(navigation, { monthKey: spendPeriodKey });
  const openWealth = () => navigateToWealth(navigation);
  const openLoan = () => navigateToLoan(navigation);
  const showEmptyImportCta = isLive && isEmpty && !loading;

  const pageRefresh = () => {
    void refreshAll('home_page');
  };

  return (
    <WebScreenBody>
      {isSampleMode && <SampleDataBanner />}

      <PageHeader
        title="Overview"
        subtitle={
          isLive
            ? showEmptyImportCta
              ? 'Import statements to see your spend and wealth snapshot'
              : 'Live data from your imports'
            : isSampleMode
              ? 'Example numbers — sign in to upload your screenshots'
              : usingDraftPreview
                ? 'Sample shell with your draft numbers overlaid'
                : 'Cumulative snapshot of income, spend, and wealth'
        }
        onRefresh={pageRefresh}
        refreshing={refreshing}
      />

      <WidgetRefreshScope refresh={pageRefresh} refreshing={refreshing}>

      {isLive && !loading ? (
        <View style={styles.importRow}>
          <SpendImportCta onImport={openImportMore} />
        </View>
      ) : null}
      <PinnedWidgetsStrip />
      <InsightStrip />
      {isLive ? <HomeSpendAlertsCard /> : null}
      {error ? (
        <ErrorRetry message={error} onRetry={pageRefresh} refreshing={refreshing} />
      ) : null}

      <WebWidgetGrid>
        <WebWidgetCell>
          <GlassCard
            style={styles.heroCard}
            // Only make the card itself pressable when there is no inner GlassButton.
            onPress={isLive ? undefined : openWealth}
            accessibilityLabel={isLive ? undefined : 'Open wealth for net worth'}
          >
            <View style={styles.heroHeader}>
              <Text style={styles.label}>Monthly take-home</Text>
              <RefreshButton compact />
            </View>
            {loading ? (
              <ActivityIndicator color={colors.accent} />
            ) : (
              <>
                <Text style={styles.value}>{formatCurrency(profile.monthlyIncome)}</Text>
                <Text style={styles.sub}>
                  Net worth {formatCurrency(netWorth.total)}
                  {usingDraftPreview ? ' · draft preview' : ''}
                </Text>
                {isLive && (
                  <View style={styles.ctaRow}>
                    <GlassButton
                      variant="secondary"
                      size="sm"
                      onPress={() => setTakeHomeOpen(true)}
                      accessibilityLabel={
                        missingTakeHome ? 'Add monthly take-home' : 'Update monthly take-home'
                      }
                    >
                      {missingTakeHome ? 'Add take-home' : 'Update'}
                    </GlassButton>
                  </View>
                )}
              </>
            )}
          </GlassCard>
        </WebWidgetCell>

        <WebWidgetCell span="full">
          <NetWorthStrip
            activeInvestments={netWorth.activeInvestments}
            passiveInvestments={netWorth.passiveInvestments}
            totalLiabilities={netWorth.totalLiabilities}
            total={netWorth.total}
            onPress={openWealth}
            onActiveInvestmentsPress={isSampleMode ? undefined : openImportMore}
            onOtherAssetsPress={
              isSampleMode ? undefined : () => openAddAccount({ isInvestment: false })
            }
          />
          {isLive ? (
            <WidgetInsightsSlot widget="home_net_worth" style={styles.widgetInsight} />
          ) : null}
        </WebWidgetCell>

        {showSpendSnapshot && (
          <WebWidgetCell span="full">
            <GlassCard accessibilityLabel="Spend snapshot">
              <View style={styles.sectionHeader}>
                <Text style={styles.sectionTitleInline}>Spend snapshot</Text>
                <View style={styles.sectionActions}>
                  <RefreshButton compact />
                  <GlassButton
                    variant="ghost"
                    size="sm"
                    onPress={openSpend}
                    accessibilityLabel="See all spending"
                  >
                    See all
                  </GlassButton>
                </View>
              </View>
              {isLive ? (
                <View style={styles.periodFilterRow}>
                  <SpendSnapshotPeriodFilter value={spendPeriod} onChange={setSpendPeriod} />
                </View>
              ) : null}
              {loading && spendCategories.length === 0 ? (
                <ActivityIndicator color={colors.accent} style={styles.snapshotLoader} />
              ) : spendCategories.length > 0 ? (
                <DonutChart
                  segments={spendCategories.map((c) => ({
                    label: c.name,
                    value: c.amount,
                    color: c.color,
                  }))}
                  centerLabel={spendPeriodLabel}
                  centerValue={formatCurrency(
                    spendCategories.reduce((sum, c) => sum + c.amount, 0),
                  )}
                />
              ) : (
                <View style={styles.snapshotEmpty}>
                  <Text style={styles.snapshotEmptyTitle}>
                    No spend {spendPeriodLabel.toLowerCase()}
                  </Text>
                  <Text style={styles.snapshotEmptyBody}>
                    Import statements or switch months to review spending.
                  </Text>
                </View>
              )}
              {isLive ? (
                <WidgetInsightsSlot widget="home_spend" style={styles.widgetInsight} />
              ) : null}
            </GlassCard>
          </WebWidgetCell>
        )}

        <WebWidgetCell span="full">
          <CreditCardsWidget
            period="month"
            periodKey={spendPeriodKey}
            sampleCategories={sampleSpendTree}
            hasHouseholdSpend={!isEmpty}
            onOpenCard={setCardTarget}
          />
        </WebWidgetCell>

        <WebWidgetCell>
          <PortfolioSnapshot assets={netWorth.assets} onPress={openWealth} />
          {isLive ? (
            <WidgetInsightsSlot widget="wealth_trends" style={styles.widgetInsight} />
          ) : null}
        </WebWidgetCell>

        <WebWidgetCell>
          <PortfolioAccountsCard
            title="Other assets"
            category="other"
            compact
            headerAction={{ label: 'Wealth', onPress: openWealth }}
          />
        </WebWidgetCell>

        <WebWidgetCell span="full">
          <HomeLoanCard onPress={openLoan} />
          {isLive ? (
            <WidgetInsightsSlot widget="home_loan" style={styles.widgetInsight} />
          ) : null}
        </WebWidgetCell>

        <WebWidgetCell span="full">
          <InvestmentsVsDebtCard
            compact
            holdings={netWorth.assets
              .filter((a) => a.isInvestment)
              .map((a) => ({
                value: a.value,
                expectedReturnPct: a.expectedReturnPct,
              }))}
            mortgageBalanceSeries={projections.mortgageBalanceSeries}
            startYear={projections.mortgageBalanceSeries[0]?.year}
            headerAction={{ label: 'Wealth', onPress: openWealth }}
          />
        </WebWidgetCell>
      </WebWidgetGrid>
      </WidgetRefreshScope>

      <EditTakeHomeModal
        visible={takeHomeOpen}
        initialValue={profile.monthlyIncome}
        onClose={() => setTakeHomeOpen(false)}
        onSaved={() => refresh()}
      />
      <CreditCardDetailModal
        card={cardTarget}
        period="month"
        periodKey={spendPeriodKey}
        isLive={isLive}
        sampleCategories={sampleSpendTree}
        onClose={() => setCardTarget(null)}
        onOpenCategory={(slug, name) => {
          setCardTarget(null);
          navigation.navigate('Spend', {
            screen: 'TransactionList',
            params: {
              categoryId: slug,
              categoryName: name,
              period: 'month',
              analyticsPeriod: 'month',
              periodKey: spendPeriodKey,
              spendAccountId: cardTarget?.spendAccountId,
              spendAccountLabel: cardTarget ? formatCardTitle(cardTarget) : undefined,
            },
          });
        }}
        onViewAll={(card) => {
          setCardTarget(null);
          navigation.navigate('Spend', {
            screen: 'TransactionList',
            params: {
              mode: 'all',
              period: 'month',
              analyticsPeriod: 'month',
              periodKey: spendPeriodKey,
              spendAccountId: card.spendAccountId,
              spendAccountLabel: formatCardTitle(card),
            },
          });
        }}
      />
    </WebScreenBody>
  );
}

const styles = StyleSheet.create({
  heroCard: {
    gap: spacing.xs,
  },
  heroHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  label: {
    ...typography.caption,
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  value: {
    ...typography.display,
    color: colors.text,
    fontSize: 36,
    fontVariant: ['tabular-nums'],
  },
  sub: {
    ...typography.body,
    color: colors.textMuted,
  },
  chip: {
    alignSelf: 'flex-start',
    marginTop: spacing.sm,
    backgroundColor: 'rgba(61, 214, 164, 0.15)',
    borderRadius: 999,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
  },
  chipText: {
    ...typography.caption,
    color: colors.accent,
    fontWeight: '600',
  },
  ctaRow: {
    marginTop: spacing.sm,
    alignItems: 'flex-start',
  },
  importRow: {
    alignItems: 'flex-start',
    marginBottom: spacing.sm,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: spacing.md,
  },
  sectionTitleInline: {
    ...typography.headline,
    color: colors.text,
  },
  sectionActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  periodFilterRow: {
    marginBottom: spacing.md,
  },
  snapshotLoader: {
    marginVertical: spacing.lg,
  },
  snapshotEmpty: {
    gap: spacing.xs,
    paddingVertical: spacing.md,
  },
  snapshotEmptyTitle: {
    ...typography.headline,
    color: colors.text,
    fontSize: 17,
  },
  snapshotEmptyBody: {
    ...typography.caption,
    color: colors.textMuted,
  },
  widgetInsight: {
    marginTop: spacing.md,
  },
  chartHint: {
    ...typography.caption,
    color: colors.textMuted,
    marginBottom: spacing.md,
    marginTop: -spacing.sm,
  },
});
